import { NgModule } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { RouterModule, Routes } from '@angular/router';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { AuthInterceptor } from '@app/_interceptors/auth.interceptor';
import { AuthGuard } from '@app/_guards/auth.guard';
import { SharedModule } from '@app/shared/shared.module';
import { CoreModule } from '@app/core/core.module';

import {
    MatDialogModule,
    MatSnackBarModule,
    MatGridListModule,
    MatTabsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatOptionModule,
    MatTableModule,
    MatListModule,
    MatProgressSpinnerModule,
    MatCheckboxModule
} from '@angular/material';

import { ProfileResolver } from './services/profile.resolver.service';

import { LessonsListModule } from '@app/teacher/components/lessons-list/lessons-list.module';
import { IndividualDataModule } from '@app/teacher/components/analysis/analysis-list-subject-home/individual-data/individual-data.module';
import { PreviewImgModule } from '@app/shared/components/preview-img/preview-img.module';

import { ProfileMainComponent } from '@profile/components/profile-main/profile-main.component';
import { ProfileInfoComponent } from '@profile/components/profile-info/profile-info.component';
import { ProfileLessonsComponent } from '@profile/components/profile-lessons/profile-lessons.component';
import { ProfileBankComponent } from '@profile/components/profile-bank/profile-bank.component';
import { ProfileAnalyticsComponent } from '@profile/components/profile-analytics/profile-analytics.component';
import { ProfileAnalyticsLessonComponent } from '@profile/components/profile-analytics/profile-analytics-lesson/profile-analytics-lesson.component';
import { ProfileSettingsComponent } from '@profile/components/profile-settings/profile-settings.component';
import { MainProfileSettingsComponent } from '@profile/components/profile-settings/main-profile-settings/main-profile-settings.component';
import { EmailProfileSettingsComponent } from '@profile/components/profile-settings/email-profile-settings/email-profile-settings.component';
import { NotificationsSettingsComponent } from '@profile/components/profile-settings/notifications-settings/notifications-settings.component';
import { PasswordProfileSettingsComponent } from '@profile/components/profile-settings/password-profile-settings/password-profile-settings.component';
import { ParentProfileSettingsComponent } from '@profile/components/profile-settings/parent-profile-settings/parent-profile-settings.component';
import { TeacherProfileSettingsComponent } from '@profile/components/profile-settings/teacher-profile-settings/teacher-profile-settings.component';
import { ProfilePurchasesComponent } from './components/profile-purchases/profile-purchases.component';
import { ProfileResultsComponent } from './components/profile-results/profile-results.component';
import { ProfileResultsLessonComponent } from './components/profile-results/profile-results-lesson/profile-results-lesson.component';
import { ProfileResultsExercisesComponent } from './components/profile-results/profile-results-exercises/profile-results-exercises.component';
import { ProfileResultsStarDescriptionComponent } from '@profile/components/profile-results/profile-results-star-desription/profile-results-star-description.component';
import { PageItemComponent } from '@app/shared/components/page-item/page-item.component';
import { ProfileAwardsComponent } from '@app/shared/components/profile-awards/profile-awards.component';
import { ProfileDataComponent } from '@profile/components/profile-data/profile-data.component';
import { NotificationsComponent } from '@profile/components/profile-notifications/profile-notifications.component';
import { NotificationComponent } from '@profile/components/profile-notifications/profile-notification/profile-notification.component';

const profileRoutes: Routes = [
    {
        path: '',
        component: ProfileMainComponent,
        canActivate: [AuthGuard],
        children: [
            { path: 'data', component: ProfileDataComponent, resolve: { profile: ProfileResolver } },
            { path: 'info', component: ProfileInfoComponent },
            { path: 'lessons', component: ProfileLessonsComponent },
            { path: 'awards/:user_id', component: ProfileAwardsComponent },
            { path: 'awards/:user_id/:award_id', component: PageItemComponent },
            { path: 'purchases', component: ProfilePurchasesComponent },
            { path: 'purchases/:purchase_id', component: PageItemComponent },
            { path: 'bank', component: ProfileBankComponent },
            { path: 'analytics', component: ProfileAnalyticsComponent },
            { path: 'analytics/:lesson_id', component: ProfileAnalyticsLessonComponent },
            { path: 'notifications', component: NotificationsComponent },
            { path: 'notifications/:notification_id', component: NotificationComponent },
            {
                path: 'results/lessons',
                component: ProfileResultsComponent,
                children: [
                    { path: ':lesson_id', component: ProfileResultsLessonComponent },
                    { path: ':lesson_id/exercises', component: ProfileResultsExercisesComponent }
                ]
            }
        ]
    },
    {
        path: 'settings',
        component: ProfileSettingsComponent,
        canActivate: [AuthGuard],
        resolve: { profile: ProfileResolver },
        children: [
            { path: 'profile', component: MainProfileSettingsComponent, resolve: { profile: ProfileResolver } },
            { path: 'parent', component: ParentProfileSettingsComponent, resolve: { profile: ProfileResolver } },
            { path: 'email', component: EmailProfileSettingsComponent, resolve: { profile: ProfileResolver } },
            { path: 'password', component: PasswordProfileSettingsComponent, resolve: { profile: ProfileResolver } },
            { path: 'notifications', component: NotificationsSettingsComponent, resolve: { profile: ProfileResolver } }
        ]
    }
];

@NgModule({
    imports: [
        HttpClientModule,
        ReactiveFormsModule,
        FormsModule,
        CoreModule,
        CommonModule,
        SharedModule,
        LessonsListModule,
        IndividualDataModule,
        PreviewImgModule,
        RouterModule.forChild(profileRoutes),
        // material
        MatDialogModule,
        MatSnackBarModule,
        MatGridListModule,
        MatTabsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatSelectModule,
        MatOptionModule,
        MatTableModule,
        MatCheckboxModule,
        MatListModule,
        MatProgressSpinnerModule
    ],
    declarations: [
        ProfileMainComponent,
        ProfileDataComponent,
        ProfileInfoComponent,
        ProfileSettingsComponent,
        MainProfileSettingsComponent,
        EmailProfileSettingsComponent,
        PasswordProfileSettingsComponent,
        ParentProfileSettingsComponent,
        TeacherProfileSettingsComponent,
        ProfileLessonsComponent,
        ProfileBankComponent,
        ProfileAnalyticsComponent,
        ProfileAnalyticsLessonComponent,
        ProfilePurchasesComponent,
        ProfileResultsComponent,
        ProfileResultsLessonComponent,
        ProfileResultsExercisesComponent,
        ProfileResultsStarDescriptionComponent,
        NotificationsSettingsComponent,
        NotificationsComponent,
        NotificationComponent
    ],
    exports: [],
    entryComponents: [ProfileResultsStarDescriptionComponent],
    providers: [
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true
        },
        DatePipe
    ]
})
export class ProfileModule {}
